import React from 'react';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand } from '../RiskAreasWidget';
import { ChevronRight } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, FreshnessBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantATable({
  areas,
  onSelect
}: Props) {
  if (areas.length === 0) return <div className="p-8 text-center text-gray-500" data-id="element-2815" data-mp="kfwu1eb9t3uo">No activities found</div>;
  return <div className="w-full overflow-x-auto" data-id="element-2816" data-mp="6os3r2tslnx0">
      <table className="w-full text-left border-collapse" data-id="element-2817" data-mp="6o06lnkjri4m">
        <thead data-id="element-2818" data-mp="bmjs5mv8arwg">
          <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50" data-id="element-2819" data-mp="bm2072flmzuc">
            <th className="px-4 py-3" data-id="element-2820" data-mp="zbmhzi00nr21">Risk Score</th>
            <th className="px-4 py-3" data-id="element-2821" data-mp="143w5yshkmh1o">Location</th>
            <th className="px-4 py-3" data-id="element-2822" data-mp="145qushvyqtb1">Date</th>
            <th className="px-4 py-3" data-id="element-2823" data-mp="3vkakbsa544ke">Status</th>
            <th className="px-4 py-3 text-center relative group/sifth" data-id="element-2824" data-mp="3vizgxtklwytt">
              <span className="underline decoration-dotted decoration-gray-400 underline-offset-2 cursor-help" data-id="element-2825" data-mp="3rkrwooqxpfdv">
                SIF
              </span>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 hidden group-hover/sifth:block w-56 bg-gray-900 text-white text-xs font-normal normal-case tracking-normal rounded-lg px-3 py-2 shadow-xl pointer-events-none text-left" data-id="element-2826" data-mp="4f10yjgniojr">
                <span className="font-bold" data-id="element-2827" data-mp="e42d1yk2slsg">SIF</span> — Serious Injury or
                Fatality. Flags activities with potential for life-threatening
                outcomes.
              </div>
            </th>
            <th className="px-4 py-3" data-id="element-2828" data-mp="n35zncigbjbp">Freshness</th>
            <th className="px-4 py-3" data-id="element-2829" data-mp="2tux1ookhfgr0">Data Quality</th>
            <th className="px-4 py-3" data-id="element-2830" data-mp="2txjjdh137cmy"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100" data-id="element-2831" data-mp="2oy6ktz0ush4j">
          {areas.map(area => {
          const config = RISK_LEVEL_CONFIG[area.riskLevel];
          const statusConfig = STATUS_CONFIG[area.status];
          const StatusIcon = statusConfig.icon;
          const band = getRiskBand(area.riskScore);
          const dataGap = hasDataGap(area);
          let rowBg = 'hover:bg-gray-50 cursor-pointer transition-colors';
          if (area.sifPotential) {
            rowBg = 'cursor-pointer transition-colors bg-red-50/60 hover:bg-red-50 border-l-4 border-red-700';
          } else if (dataGap) {
            rowBg = 'cursor-pointer transition-colors bg-amber-50/30 hover:bg-amber-50/50 border-l-4 border-dashed border-amber-400';
          } else if (area.status === 'closed') {
            rowBg += ' bg-emerald-50/30 opacity-75 border-l-4 border-transparent';
          } else if (area.status === 'measures_taken') {
            rowBg += ' bg-amber-50/30 border-l-4 border-transparent';
          } else {
            rowBg += ' border-l-4 border-transparent';
          }
          return <tr key={area.id} onClick={() => onSelect(area)} className={rowBg} data-id="element-2832" data-mp="3eztxnsgyns1o">
                <td className="px-4 py-3" data-id="element-2833" data-mp="3b1e6dcdujxig">
                  <div className="flex items-center gap-2" data-id="element-2834" data-mp="360ytsn1e2u1j">
                    <div className={`w-10 py-1 rounded ${config.bgColor} flex flex-col items-center justify-center`} data-id="element-2835" data-mp="362jnybjskayv">
                      <span className={`text-[10px] font-bold ${config.color}`} data-id="element-2836" data-mp="2krrajupn2uc">
                        {band.short}
                      </span>
                    </div>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`} data-id="element-2837" data-mp="3ozumftxln0d9">
                      {area.riskScore}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-2838" data-mp="52tgi6n6atpi">
                  <div className="text-sm font-semibold text-gray-900" data-id="element-2839" data-mp="51s3hwmljx55">
                    {area.project}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-2840" data-mp="a37rnxk2mjrr">
                    {area.floor} · {area.location}
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-2841" data-mp="10gbxlk5x1ub">
                  <ScheduledDateBadge date={area.scheduledDate} data-id="element-2842" data-mp="11hz2oegdese" />
                </td>
                <td className="px-4 py-3" data-id="element-2843" data-mp="orccp86lj7dp">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full ${statusConfig.bgColor} ${statusConfig.color}`} data-id="element-2844" data-mp="sse9zjgyd9d1">
                    <StatusIcon size={10} data-id="element-2845" data-mp="jzu9edh3oupk" />
                    {statusConfig.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-center" data-id="element-2846" data-mp="17on98l0ufmpg">
                  {area.sifPotential ? <SifBadge data-id="element-2847" data-mp="1chgdz8wbav1j" /> : <span className="text-gray-300" data-id="element-2848" data-mp="1ci7wbi0wew9v">-</span>}
                </td>
                <td className="px-4 py-3" data-id="element-2849" data-mp="1lsudxckah9v5">
                  <FreshnessBadge lastUpdated={area.lastUpdated} data-id="element-2850" data-mp="1hsk75xooaff8" />
                </td>
                <td className="px-4 py-3" data-id="element-2851" data-mp="1vl9rlxojzca8">
                  <DataConfidenceBadge confidence={area.dataConfidence} data-id="element-2852" data-mp="1vk8elnnz8fpv" />
                </td>
                <td className="px-4 py-3 text-right" data-id="element-2853" data-mp="1qmp2ouer4f13">
                  <ChevronRight size={16} className="text-gray-300 inline-block" data-id="element-2854" data-mp="12vt6ll39ixo1" />
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}